import { agenticContentForUrl } from '~/modules/helpers/agenticContentParamHelper';
import { getCurrentLocalePrefix } from '~/modules/helpers/localeHelper';
import { CONSULTING_SEARCH_CONTEXT } from '~/modules/helpers/searchContextHelper';
import { EXPLORE_BASE_ROUTE, Routes } from '~/modules/routes';
import type { FilterParams, SearchParams } from '../stores/searchStore';

const APPS_SEARCH_TYPE = 'apps';
const SLACK_SEARCH_TYPE = 'slack';

interface FilterExtraParam {
    key: string;
    value: string[];
}

interface ExtraUrlParams {
    sort?: string;
    type?: string;
    /** When true, emit type=apps in the URL even though it's the default (used for type=apps&tech=slack). */
    forceType?: boolean;
    filter?: FilterExtraParam;
    resetState?: boolean;
    expand?: string[] | string | undefined;
    context?: string;
}

type UrlParam = { name: string; value: string };

/**
 * Convert internal search state to the public URL contract. Slack is an internal
 * tab type whose public representation remains type=apps&tech=slack.
 */
const normaliseParamsForUrl = (
    searchParams: SearchParams,
): {
    searchParams: SearchParams;
    urlParams: { type: string | undefined; forceType?: boolean };
} => {
    if (searchParams.type === SLACK_SEARCH_TYPE) {
        const tech = searchParams.filterParams?.tech;
        const techWithSlack = tech?.includes('slack') ? tech : [...(tech ?? []), 'slack'];
        return {
            searchParams: {
                ...searchParams,
                filterParams: { ...searchParams.filterParams, tech: techWithSlack },
            },
            urlParams: { type: APPS_SEARCH_TYPE, forceType: true },
        };
    }
    return { searchParams, urlParams: { type: searchParams.type } };
};

const arrayNotEmptyNotEmptyValue = (value: string[]): boolean => {
    if (!value || value.length === 0) {
        return false;
    }
    for (const entry of value) {
        if (entry) {
            return true;
        }
    }
    return false;
};

const agenticContentValuesForUrl = (values: string[]): string[] => {
    return agenticContentForUrl(values) ?? values;
};

const buildFilterParams = (filterParams: FilterParams, filter?: FilterExtraParam): UrlParam[] => {
    return Object.entries(filterParams).reduce((filters, [key, value]) => {
        if (key === filter?.key && arrayNotEmptyNotEmptyValue(filter?.value)) {
            const overrideForUrl = key === 'agenticContent' ? agenticContentValuesForUrl(filter.value) : filter.value;
            return [...filters, { name: key, value: encodeURIComponent(overrideForUrl.join(';')) }];
        }
        if (key !== filter?.key) {
            if (value) {
                const valueForUrl =
                    key === 'agenticContent' && Array.isArray(value) ? agenticContentValuesForUrl(value) : value;
                const valueAsString = Array.isArray(valueForUrl) ? valueForUrl.join(';') : valueForUrl;
                return [...filters, { name: key, value: encodeURIComponent(valueAsString) }];
            }
        }
        return filters;
    }, [] as UrlParam[]);
};

const buildExpandParam = (expand: string | string[]): UrlParam => {
    return {
        name: 'expand',
        value: encodeURIComponent(Array.isArray(expand) ? expand.join(',') : expand),
    };
};

const buildSortParam = (sortOption?: string): UrlParam | null => {
    return sortOption && sortOption !== 'relevant' ? { name: 'sort', value: sortOption } : null;
};

const buildTypeParam = (type?: string, force: boolean = false): UrlParam | null => {
    if (!type || (type === 'apps' && !force)) {
        return null;
    }
    return { name: 'type', value: type };
};

const buildKeywordParam = (encodedKeyword?: string): UrlParam | null => {
    return encodedKeyword ? { name: 'keywords', value: encodedKeyword } : null;
};

const buildUrl = (baseUrl: string, params: Array<UrlParam | null>) => {
    const validParams = params.filter((param): param is UrlParam => Boolean(param));
    if (validParams.length > 0) {
        const paramString = validParams.map((param) => `${param.name}=${param.value}`).join('&');
        return `${baseUrl}?${paramString}`;
    }
    return baseUrl;
};

const buildGenericNavigationUrl = (
    baseUrl: string,
    encodedKeyword?: string,
    sortParam?: string,
    filterParams?: FilterParams,
    {
        sort = undefined,
        type = undefined,
        forceType = false,
        filter = undefined,
        resetState = false,
        expand = undefined,
        context = undefined,
    }: ExtraUrlParams = {},
): string => {
    const sortBy = sort || sortParam;
    const baseParams: Array<UrlParam | null> = [
        buildKeywordParam(encodedKeyword),
        buildTypeParam(type, forceType),
        context ? { name: 'context', value: context } : null,
    ];

    if (resetState) {
        return buildUrl(baseUrl, baseParams);
    }

    const completeParams = [...baseParams, buildSortParam(sortBy), ...buildFilterParams(filterParams || {}, filter)];

    if (expand) {
        completeParams.push(buildExpandParam(expand));
    }

    return buildUrl(baseUrl, completeParams);
};

const buildNavigationUrl = (searchParams: SearchParams, options: ExtraUrlParams = {}): string => {
    const {
        sort = undefined,
        type = undefined,
        forceType = false,
        filter = undefined,
        resetState = false,
        expand = undefined,
    } = options;
    const requestedType = Object.prototype.hasOwnProperty.call(options, 'type') ? type : searchParams.type;
    const isCanonicalSlackOverride =
        searchParams.type === SLACK_SEARCH_TYPE &&
        requestedType === APPS_SEARCH_TYPE &&
        forceType &&
        searchParams.filterParams?.tech?.includes(SLACK_SEARCH_TYPE);
    const isSlackNavigation = requestedType === SLACK_SEARCH_TYPE || isCanonicalSlackOverride;
    const normalised = isSlackNavigation
        ? normaliseParamsForUrl({ ...searchParams, type: SLACK_SEARCH_TYPE })
        : { searchParams, urlParams: { type: requestedType, forceType } };
    const normalisedFilter =
        isSlackNavigation && filter?.key === 'tech'
            ? { ...filter, value: filter.value.includes('slack') ? filter.value : [...filter.value, 'slack'] }
            : filter;
    // resetState normally strips every filter. Slack's technology token is part of
    // its public type encoding, so retain that one required token while resetting.
    const isResettingSlack = isSlackNavigation && resetState;
    const filterParams = isResettingSlack ? { tech: ['slack'] } : normalised.searchParams.filterParams;
    const encodedKeyword = normalised.searchParams.keyword
        ? encodeURIComponent(normalised.searchParams.keyword)
        : undefined;
    const localePrefix = getCurrentLocalePrefix();
    return buildGenericNavigationUrl(
        `${localePrefix}/${Routes.SEARCH}`,
        encodedKeyword,
        isResettingSlack ? undefined : normalised.searchParams.sort,
        filterParams,
        {
            sort: isResettingSlack ? undefined : sort,
            type: normalised.urlParams.type,
            forceType: normalised.urlParams.forceType,
            filter: isResettingSlack ? undefined : normalisedFilter,
            resetState: isResettingSlack ? false : resetState,
            expand: isResettingSlack ? undefined : expand,
            context: normalised.searchParams.isConsulting ? CONSULTING_SEARCH_CONTEXT : undefined,
        },
    );
};

const buildExploreNavigationUrl = (
    bucket: string,
    searchParams: SearchParams,
    {
        sort,
        type,
        filter,
        resetState,
        advancedFilters,
    }: {
        sort?: string;
        type?: string;
        filter?: { key: string; value: string[] };
        resetState?: boolean;
        advancedFilters?: boolean;
    } = {},
): string => {
    const advancedFiltersParam = advancedFilters ? '&advancedFilters=true' : '';
    const localePrefix = getCurrentLocalePrefix();
    return (
        buildGenericNavigationUrl(
            `${localePrefix}/${EXPLORE_BASE_ROUTE}/${bucket}`,
            undefined,
            undefined,
            searchParams.filterParams,
            {
                sort,
                type,
                filter,
                resetState,
            },
        ) + advancedFiltersParam
    );
};

const buildConsultingNavigationUrl = (
    searchParams: SearchParams,
    { sort = undefined, filter = undefined }: Pick<ExtraUrlParams, 'sort' | 'filter'> = {},
): string => {
    const localePrefix = getCurrentLocalePrefix();
    return buildGenericNavigationUrl(
        `${localePrefix}/${Routes.CONSULTING_HOME}`,
        undefined,
        searchParams.sort,
        searchParams.filterParams,
        { sort, filter },
    );
};

export { buildConsultingNavigationUrl, buildExploreNavigationUrl, buildNavigationUrl, normaliseParamsForUrl };
